import { useCallback, useState } from 'react'

import type { MathProvenance } from '../api/client'

/**
 * Proof of math provenance: the sha256 of the file currently playing on
 * the server.
 *
 * Why this is in the UI: a version like "0.3.0" can be written into the
 * config at will, but the weights cannot be swapped so that the hash
 * still matches. The player (and the auditor) sees the very same number
 * that is in the release manifest and in the PAR sheet, so "which math is
 * spinning right now" is verified by eye rather than taken on trust.
 *
 * kind='config' means an unfrozen working file — that is a development
 * state, and it is marked in a different colour so that such a build does
 * not reach production silently.
 */
export function MathBadge({ math, version }: { math: MathProvenance; version: string }): React.JSX.Element {
  const [copied, setCopied] = useState(false)

  const handleCopy = useCallback(() => {
    // The clipboard is unavailable outside https/localhost — then nothing
    // happens at all; the hash is still visible in the title and on screen.
    void navigator.clipboard?.writeText(math.sha256).then(
      () => {
        setCopied(true)
        setTimeout(() => setCopied(false), 1200)
      },
      () => undefined,
    )
  }, [math.sha256])

  const isRelease = math.kind === 'release'
  const short = math.sha256 ? math.sha256.slice(0, 12) : '—'
  const title = math.sha256
    ? `${math.file}\nsha256: ${math.sha256}\n` +
      (isRelease
        ? `frozen ${math.releasedAt ?? '—'}`
        : 'WORKING config — not a frozen release') +
      '\nClick to copy the full hash'
    : 'Hash unavailable'

  return (
    <button
      type="button"
      className={`mathbadge ${isRelease ? 'mathbadge--release' : 'mathbadge--dev'}`}
      onClick={handleCopy}
      title={title}
    >
      <span className="mathbadge__ver">v{version}</span>
      <span className="mathbadge__hash">{copied ? 'copied' : short}</span>
      {!isRelease && <span className="mathbadge__warn">dev</span>}
    </button>
  )
}
